import type { SupportedLocale } from '../hooks/useLocale';

// ─── Month names (Gregorian) ───────────────────────────────────────
//
// The date pickers (`DatePickerField`, `web-pickers` WebDatePicker) STORE their
// value in canonical English ("Jun 2026" / "12 June 1998" / "2026") because the
// downstream parsers: `parsePickerDate`/`parsePeriodString` in cv-mapping.ts and
// `parsePickerDate` in experience-validation.ts: only accept English month
// tokens. Storing localized month names there would silently break date saving
// and validation. So localization here is DISPLAY-ONLY: pick the label array for
// the active locale for grids, and translate the month token inside a stored
// English value for the trigger label via `localizeDateLabel`.

export const EN_MONTHS_SHORT: readonly string[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const EN_MONTHS_LONG: readonly string[] = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Arabic Gregorian month names. Arabic has no short/long distinction here, so
// the same array serves both the abbreviated and full-name slots.
export const AR_MONTHS: readonly string[] = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

/** Display labels for the abbreviated month slot (month grids). */
export function monthLabelsShort(locale: SupportedLocale): readonly string[] {
  return locale === 'ar' ? AR_MONTHS : EN_MONTHS_SHORT;
}

/** Display labels for the full month-name slot (day-mode headers). */
export function monthLabelsLong(locale: SupportedLocale): readonly string[] {
  return locale === 'ar' ? AR_MONTHS : EN_MONTHS_LONG;
}

/**
 * Translate the month token inside a stored (English) picker value for display.
 * - English locale (or empty/unknown value): returned unchanged, so existing
 *   English behaviour is untouched.
 * - Arabic: only the month word is swapped; digits (day/year) stay in Western
 *   numerals and the structure ("Jun 2026", "12 June 1998", "2026") is
 *   preserved. The STORED value stays English: this is display only.
 *
 * Token-exact (split on spaces) so "Jun" is never matched inside "June" etc.
 */
export function localizeDateLabel(value: string, locale: SupportedLocale): string {
  if (!value || locale !== 'ar') return value;
  return value
    .split(' ')
    .map((token) => {
      const short = EN_MONTHS_SHORT.indexOf(token);
      if (short >= 0) return AR_MONTHS[short];
      const long = EN_MONTHS_LONG.indexOf(token);
      if (long >= 0) return AR_MONTHS[long];
      return token;
    })
    .join(' ');
}
